import { useId, useRef } from "react";
import type { ReactNode } from "react";
import { useDialogBehavior } from "./useDialogBehavior.js";
import "./Sheet.css";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

export function Sheet({ open, onClose, title, children }: SheetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const generatedId = useId();
  const titleId = title ? `${generatedId}-title` : undefined;

  useDialogBehavior(open, onClose, containerRef);

  if (!open) return null;

  return (
    <div
      className="ui-sheet-backdrop"
      // Backdrop click closes, but a click that started inside the sheet and
      // bubbled here (e.g. text selection drag ending over the backdrop)
      // should not — only react when the backdrop itself was the target.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={containerRef}
        className="ui-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="ui-sheet__grab" aria-hidden="true" />
        {title && (
          <h2 className="ui-sheet__title" id={titleId}>
            {title}
          </h2>
        )}
        <div className="ui-sheet__content">{children}</div>
      </div>
    </div>
  );
}
